<template>
  <vx-card id="invoice-container" v-if="ready">
    <vs-row class="mb-base">
      <h4 class="mr-2">
        {{ $t(`users.genders.name.${request.gender}`) }} {{ request.firstName }} {{ request.lastName }}
      </h4>
      <p>{{ request.age | age_range }}</p>
    </vs-row>
    <div class="vx-row mb-base">
      <div class="vx-col w-full md:w-1/4 sm:w-1/2">
        <vs-row class="font-semibold">{{ $t('users.phone') }}</vs-row>
        <vs-row class="mt-3">{{ request.phoneNumber | persian_digits }}</vs-row>
      </div>
      <div class="vx-col w-full md:w-1/4 sm:w-1/2">
        <vs-row class="font-semibold">{{ $t('users.emergency_phone') }}</vs-row>
        <vs-row class="mt-3">{{ request.emergencyNumber | persian_digits }}</vs-row>
      </div>
      <div class="vx-col w-full md:w-1/4 sm:w-1/2">
        <vs-row class="font-semibold">{{ $t('entity.created_at') }}</vs-row>
        <vs-row class="mt-3">{{ dataLocal.created_at | datetime }}</vs-row>
      </div>
      <div class="vx-col w-full md:w-1/4 sm:w-1/2">
        <vs-row class="font-semibold">{{ $t('entity.state') }}</vs-row>
        <vs-row class="mt-3"><cell-badges :data="dataLocal.state" local="invoices.states." /> </vs-row>
      </div>
    </div>
    <vs-table hoverFlat :data="dataLocal.items" v-if="!reloadTable">
      <template slot="thead">
        <vs-th>{{ $t('request_forms.service_name') }}</vs-th>
        <vs-th>{{ $t('request_forms.price') }} </vs-th>
        <vs-th>{{ $t('request_forms.number_of_service') }} </vs-th>
        <vs-th>{{ $t('request_forms.total_price') }}</vs-th>
        <vs-th>{{ $t('forms.actions') }}</vs-th>
      </template>
      <template slot-scope="{ data }">
        <tbody v-if="data.length">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              {{ tr.service_name }}
            </vs-td>
            <vs-td>
              {{ tr.price | persian_digits }}
            </vs-td>
            <vs-td> {{ tr.count | persian_digits }} {{ $t(`units.${tr.unit}`) }} </vs-td>
            <vs-td>
              {{ (tr.price * tr.count) | persian_digits }}
            </vs-td>
            <vs-td>
              <feather-icon
                icon="EditIcon"
                svgClasses="w-5 h-5 hover:text-danger stroke-current"
                class="ml-2"
                @click.stop="openEditPrompt(indextr)"
              />
              <feather-icon
                icon="TrashIcon"
                svgClasses="w-5 h-5 hover:text-danger stroke-current"
                class="ml-2"
                v-if="!tr.type.startsWith('!')"
                @click.stop="openConfirm(indextr)"
              />
            </vs-td>
          </vs-tr>
        </tbody>
        <tfoot>
          <vs-tr class="font-semibold">
            <vs-td colspan="2">
              <vs-button
                icon-pack="feather"
                icon="icon-plus"
                type="border"
                size="small"
                class="mx-auto px-12"
                @click="activeAddPrompt = true"
              >
                {{ $t('invoices.add_item') }}
              </vs-button>
            </vs-td>
            <vs-td class="text-right">{{ $t('invoices.total') }}:</vs-td>
            <vs-td>{{
              dataLocal.items.reduce((accum, item) => accum + item.price * item.count, 0) | persian_digits
            }}</vs-td>
            <vs-td></vs-td>
          </vs-tr>
        </tfoot>
      </template>
    </vs-table>
    <div slot="footer">
      <vs-row vs-justify="flex-end">
        <vs-button color="success" @click="updateInvoice()">
          {{ $t('forms.update') }}
        </vs-button>
      </vs-row>
    </div>
    <vs-popup :title="$t('invoices.add_item')" :active.sync="activeAddPrompt" class="service-popup">
      <div class="w-full">
        <label class="vs-input--label">{{ $t('request_forms.service_name') }}</label>
        <v-select
          v-model="newItem.name"
          :clearable="false"
          :options="availableServiceOptions"
          :reduce="option => option.value"
          name="name"
          v-validate="'required'"
          :data-vv-as="$t('request_forms.service_name')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
          class="w-full"
        />
        <span class="text-danger text-sm" v-show="errors.has('name')">{{ errors.first('name') }}</span>
      </div>
      <div class="w-full">
        <vs-input
          type="number"
          class="w-full mt-4"
          :label="$t('invoices.count')"
          v-model="newItem.count"
          @keyup="newItem.count = translateNumbers(newItem.count)"
          v-validate="'required'"
          :data-vv-as="$t('invoices.count')"
          name="count"
        />
        <span class="text-danger text-sm" v-show="errors.has('count')">{{ errors.first('count') }}</span>
      </div>
      <div class="w-full">
        <label class="vs-input--label">{{ $t('admin.service_subject.unit') }}</label>
        <v-select
          v-model="newItem.unit"
          :clearable="false"
          :options="unitOptions"
          :reduce="option => option.value"
          name="unit"
          v-validate="'required'"
          :data-vv-as="$t('admin.service_subject.unit')"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
          class="w-full"
        />
        <span class="text-danger text-sm" v-show="errors.has('unit')">{{ errors.first('unit') }}</span>
      </div>
      <div class="w-full">
        <vs-input
          type="number"
          class="w-full mt-4"
          :label="$t('invoices.fee_price')"
          v-model="newItem.price"
          @keyup="newItem.price = translateNumbers(newItem.price)"
          v-validate="'required'"
          :data-vv-as="$t('invoices.fee_price')"
          name="price"
        />
        <span class="text-danger text-sm" v-show="errors.has('price')">{{ errors.first('price') }}</span>
      </div>
      <vs-divider />
      <vs-row>
        <div class="my-auto">
          {{ $t('request_forms.total_price') }}:
          {{ newItem.price * newItem.count > 0 ? newItem.price * newItem.count : '0' | persian_digits }}
        </div>
        <vs-button color="success" class="ml-auto" @click="addItem" :disabled="!validateAddItem">
          {{ $t('forms.add') }}
        </vs-button>
      </vs-row>
    </vs-popup>
    <vs-popup :title="$t('invoices.edit_item') + ' ' + editItem.service_name" :active.sync="activeEditPrompt">
      <div class="w-full">
        <vs-input
          type="number"
          class="w-full mt-4"
          :label="$t('invoices.count')"
          v-model="editItem.count"
          @keyup="editItem.count = translateNumbers(editItem.count)"
          name="edit_count"
        />
      </div>
      <div class="w-full">
        <label class="vs-input--label">{{ $t('admin.service_subject.unit') }}</label>
        <v-select
          v-model="editItem.unit"
          :clearable="false"
          :options="unitOptions"
          :reduce="option => option.value"
          name="edit_unit"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
          class="w-full"
        />
      </div>
      <div class="w-full">
        <vs-input
          type="number"
          class="w-full mt-4"
          :label="$t('invoices.fee_price')"
          v-model="editItem.price"
          @keyup="editItem.price = translateNumbers(editItem.price)"
          name="edit_price"
        />
      </div>
      <vs-divider />
      <vs-row>
        <div class="my-auto">
          {{ $t('request_forms.total_price') }}:
          {{ editItem.price * editItem.count > 0 ? editItem.price * editItem.count : '0' | toman }}
        </div>
        <vs-button color="success" class="ml-auto" @click="updateItem" :disabled="!validateEditItem">
          {{ $t('forms.edit') }}
        </vs-button>
      </vs-row>
    </vs-popup>
  </vx-card>
</template>

<script>
import CellBadges from '@/views/components/common/table-cells/CellBadges'
import i18n from '@/i18n/i18n'
import { TranslateNumbers } from '@/utils'

export default {
  name: 'BusinessInvoiceEdit',
  components: { CellBadges },
  data() {
    return {
      dataLocal: {},
      request: {},
      availableServices: [],
      ready: null,
      newItem: {},
      reloadTable: false,
      activeAddPrompt: false,
      editItemIndex: null,
      editItem: {},
      activeEditPrompt: false
    }
  },
  computed: {
    stateOptions() {
      return this.$store.getters['enum/selectOptions']('InvoiceState', 'invoices.states')
    },
    unitOptions() {
      return this.$store.getters['enum/selectOptions']('InvoiceUnit', 'units')
    },
    availableServiceOptions() {
      const options = []
      // eslint-disable-next-line no-unused-vars
      this.availableServices.forEach(value => {
        Object.keys(value.services).forEach(service => {
          options.push({
            label:
              i18n.t(`places.categories.${value.category}`) +
              ' - ' +
              i18n.t(`places.subjects.${value.subject}`) +
              ' - ' +
              i18n.t(`health_services.${value.subject}.${service}`),
            value: `health:${value.category}:${value.subject}@${service}`
          })
        })
      })
      return options
    },
    validateAddItem() {
      return this.newItem.name && this.newItem.price > 0 && this.newItem.count > 0 && this.newItem.unit
    },
    validateEditItem() {
      return this.editItem.name && this.editItem.price > 0 && this.editItem.count > 0 && this.editItem.unit
    }
  },
  watch: {
    reloadTable(value) {
      this.$nextTick(() => {
        if (value) this.reloadTable = !value
      })
    }
  },
  methods: {
    updateInvoice() {
      this.$store
        .dispatch('invoice/editInvoice', {
          id: this.dataLocal.id,
          data: { items: this.dataLocal.items, userId: this.dataLocal.userId }
        })
        .then(() => {
          this.$router.push({ name: 'business-invoice-view', params: { id: this.dataLocal.id } })
        })
    },
    addItem() {
      const [more, name] = this.newItem.name.split('@')
      const [type, category, subject] = more.split(':')
      this.newItem.type = type
      this.newItem.category = category
      this.newItem.subject = subject
      this.newItem.service_name = i18n.t(`health_services.${subject}.${name}`)
      this.dataLocal.items.push(this.newItem)
      this.newItem = {}
      this.activeAddPrompt = false
    },
    openEditPrompt(index) {
      this.editItemIndex = index
      this.editItem = JSON.parse(JSON.stringify(this.dataLocal.items[index]))
      this.activeEditPrompt = true
    },
    updateItem() {
      this.dataLocal.items[this.editItemIndex] = this.editItem
      this.editItemIndex = null
      this.editItem = {}
      this.reloadTable = true
      this.activeEditPrompt = false
    },
    openConfirm(index) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('button.delete'),
        text: this.$t('messages.are_you_sure'),
        accept: () => {
          this.dataLocal.items.splice(index, 1)
        },
        acceptText: this.$t('button.delete'),
        cancelText: this.$t('button.cancel')
      })
    },
    translateNumbers(val) {
      return TranslateNumbers(val)
    }
  },
  created() {
    this.$vs.loading()

    this.$store
      .dispatch('invoice/fetchInvoice', this.$route.params.id)
      .then(res => {
        this.dataLocal = JSON.parse(JSON.stringify(res.data))
        if (this.dataLocal.state !== 'pending') {
          this.$notifyError(this.$t('server.error.token_expiration'))
          this.$router.push({ name: 'business-invoice-view', params: { id: this.dataLocal.id } })
          return
        }

        this.$store
          .dispatch('request/fetchHealthRequestById', this.dataLocal.request.id)
          .then(res => {
            this.request = JSON.parse(JSON.stringify(res.data))
          })
          .catch(() => this.$vs.loading.close())

        this.$store
          .dispatch('config/fetchConfig', 'Place:Category:Subject')
          .then(resp => {
            const serviceDetails = this.$store.getters['config/provinceServiceDetails']
            Object.keys(resp.data.value).forEach((category, index) => {
              if (index === 0)
                this.availableServices.push({
                  services: serviceDetails['common'],
                  subject: 'common',
                  category: 'common'
                })
              resp.data.value[category].forEach(s => {
                if (serviceDetails[s])
                  this.availableServices.push({
                    services: serviceDetails[s],
                    subject: s,
                    category: category
                  })
              })
              if (index === Object.keys(resp.data.value).length - 1) this.ready = true
            })
          })
          .finally(() => this.$vs.loading.close())
      })
      .catch(() => this.$vs.loading.close())
  }
}
</script>

<style lang="scss">
.service-popup .vs-popup--content {
  overflow: visible;
}
</style>
